/**
 * Tri, recherche, filtres propres et pagination d'UN tableau paginé par le
 * serveur (contrat §10.2-3 : paramètres préfixés par la clé du tableau, que
 * le client v2 ajoute à l'envoi).
 *
 * L'état est rattaché à une `resetKey` (filtres de l'écran) : quand ils
 * changent, la page repart à 1 DANS LE MÊME RENDU — pas d'effet qui
 * remettrait la page à 1 après coup, donc pas de requête intermédiaire sur
 * l'ancienne page (F-FE-01).
 */
import { useCallback, useState } from 'react';
import type { SortOrder, TableFilters, TableQuery } from '../../../api/monitoring/v2';
import type { ServerTableState } from './DataTable';

export const DEFAULT_PAGE_SIZE = 25;

type Stored = { key: string; state: ServerTableState; q: string; filters: Record<string, string | undefined> };

export const useServerTable = (resetKey: string, initial?: Partial<ServerTableState>) => {
    const [stored, setStored] = useState<Stored>({
        key: resetKey,
        state: { page: 1, pageSize: DEFAULT_PAGE_SIZE, ...initial },
        q: '',
        filters: {},
    });

    // Filtres de l'écran changés : on garde tri, taille, recherche et filtres
    // propres du tableau, on revient page 1.
    const current: Stored = stored.key === resetKey ? stored : { ...stored, key: resetKey, state: { ...stored.state, page: 1 } };

    const setState = useCallback(
        (next: ServerTableState) => setStored((prev) => ({ ...prev, key: resetKey, state: next })),
        [resetKey]
    );
    const setQuery = useCallback(
        (q: string) => setStored((prev) => ({ ...prev, key: resetKey, q, state: { ...prev.state, page: 1 } })),
        [resetKey]
    );
    /** Filtre propre du tableau (§10.3) ; `undefined` le retire. */
    const setFilter = useCallback(
        (name: string, value: string | undefined) =>
            setStored((prev) => ({
                ...prev,
                key: resetKey,
                filters: { ...prev.filters, [name]: value || undefined },
                state: { ...prev.state, page: 1 },
            })),
        [resetKey]
    );

    const { page, pageSize, sort, order } = current.state;
    const activeFilters = Object.fromEntries(Object.entries(current.filters).filter(([, v]) => v !== undefined)) as Record<
        string,
        string
    >;

    /** Tri, recherche et filtres, sans pagination : c'est aussi ce qu'on exporte. */
    const filters: TableFilters = {
        ...(sort ? { sort, order: order as SortOrder } : {}),
        ...(current.q ? { q: current.q } : {}),
        ...activeFilters,
    };
    /** Paramètres complets du tableau pour la requête de l'écran. */
    const query: TableQuery & Record<string, string | number | undefined> = {
        ...filters,
        limit: pageSize,
        offset: (page - 1) * pageSize,
    };

    return { state: current.state, q: current.q, filterValues: current.filters, setState, setQuery, setFilter, filters, query };
};
